import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * Main Layout Component
 *
 * Provides consistent navigation header across all authenticated pages.
 * Includes responsive slide-in drawer for mobile navigation.
 */
export function Layout({ children }: { children: React.ReactNode }) {
	const { user, signOut } = useAuth();
	const location = useLocation();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const navigation = [
		{ name: 'Harian', href: '/entries', roles: ['viewer', 'editor', 'admin', 'superadmin'] },
		{ name: 'Mingguan', href: '/weekly', roles: ['viewer', 'editor', 'admin', 'superadmin'] },
		{ name: 'Histori Medis', href: '/medical-history', roles: ['viewer', 'editor', 'admin', 'superadmin'] },
		{ name: 'Pengeluaran', href: '/expenses', roles: ['admin', 'superadmin'] },
		{ name: 'Catat Data', href: '/log', roles: ['editor', 'admin', 'superadmin'] },
		{ name: 'Kelola Pengguna', href: '/users', roles: ['admin', 'superadmin'] },
	];

	const filteredNav = navigation.filter((item) =>
		item.roles.includes(user?.role || 'viewer')
	);

	const isActive = (href: string) => location.pathname === href;

	// Prevent body scroll when drawer is open
	useEffect(() => {
		if (mobileMenuOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [mobileMenuOpen]);

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Navigation Header - Fixed on mobile for easy access */}
			<nav className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-30 sm:relative">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between h-16">
						{/* Left side: Mobile hamburger + Brand */}
						<div className="flex items-center">
							{/* Mobile menu button - LEFT side */}
							<button
								type="button"
								onClick={() => setMobileMenuOpen(true)}
								className="sm:hidden inline-flex items-center justify-center p-2 -ml-2 mr-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
								aria-label="Buka menu navigasi"
							>
								<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
								</svg>
							</button>

							{/* Brand */}
							<Link to="/" className="flex items-center flex-shrink-0">
								<span className="text-xl font-bold text-blue-600">Mami Tracker</span>
							</Link>

							{/* Desktop Navigation */}
							<div className="hidden sm:ml-8 sm:flex sm:space-x-4">
								{filteredNav.map((item) => (
									<Link
										key={item.name}
										to={item.href}
										className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition ${
											isActive(item.href)
												? 'bg-blue-50 text-blue-700'
												: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
										}`}
									>
										{item.name}
									</Link>
								))}
							</div>
						</div>

						{/* Right: User info + Sign out (desktop only) */}
						<div className="hidden sm:flex sm:items-center sm:space-x-4">
							<div className="text-sm text-gray-600">
								<span className="font-medium text-gray-900">{user?.name}</span>
								<span className="mx-1">·</span>
								<span className={`px-2 py-0.5 text-xs font-medium rounded ${
									user?.role === 'superadmin'
										? 'bg-purple-100 text-purple-700'
										: user?.role === 'admin'
											? 'bg-orange-100 text-orange-700'
											: user?.role === 'editor'
												? 'bg-blue-100 text-blue-700'
												: 'bg-gray-100 text-gray-700'
								}`}>
									{user?.role}
								</span>
							</div>
							<button
								onClick={signOut}
								className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition"
							>
								Keluar
							</button>
						</div>
					</div>
				</div>
			</nav>

			{/* Mobile Slide-in Drawer */}
			{/* Overlay backdrop */}
			<div
				className={`fixed inset-0 bg-black/50 z-40 sm:hidden transition-opacity duration-300 ${
					mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
				}`}
				onClick={() => setMobileMenuOpen(false)}
				aria-hidden={!mobileMenuOpen}
			/>

			{/* Drawer */}
			<nav
				className={`fixed top-0 left-0 h-full w-72 bg-white shadow-lg z-50 sm:hidden transform transition-transform duration-300 ease-in-out ${
					mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
				}`}
				aria-hidden={!mobileMenuOpen}
				aria-label="Menu navigasi mobile"
				role="dialog"
				aria-modal="true"
			>
				{/* Drawer Header with close button */}
				<div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
					<span className="text-lg font-bold text-blue-600">Mami Tracker</span>
					<button
						type="button"
						onClick={() => setMobileMenuOpen(false)}
						className="p-2 -mr-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
						aria-label="Tutup menu"
					>
						<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* User Info Section */}
				<div className="px-4 py-4 border-b border-gray-200 bg-gray-50">
					<div className="flex items-center">
						{/* User initials avatar */}
						<div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
							{user?.name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || 'U'}
						</div>
						<div className="ml-3">
							<p className="text-sm font-medium text-gray-900">{user?.name}</p>
							<p className="text-xs text-gray-500 truncate max-w-[180px]">{user?.email}</p>
						</div>
					</div>
					<span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded ${
						user?.role === 'superadmin'
							? 'bg-purple-100 text-purple-700'
							: user?.role === 'admin'
								? 'bg-orange-100 text-orange-700'
								: user?.role === 'editor'
									? 'bg-blue-100 text-blue-700'
									: 'bg-gray-100 text-gray-700'
					}`}>
						{user?.role}
					</span>
				</div>

				{/* Navigation Links */}
				<div className="flex-1 py-4">
					{filteredNav.map((item) => (
						<Link
							key={item.name}
							to={item.href}
							onClick={() => setMobileMenuOpen(false)}
							className={`flex items-center px-4 py-3 text-base font-medium transition ${
								isActive(item.href)
									? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
									: 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent'
							}`}
						>
							{item.name}
						</Link>
					))}
				</div>

				{/* Sign Out Button at Bottom */}
				<div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white drawer-safe-area">
					<button
						onClick={() => {
							setMobileMenuOpen(false);
							signOut();
						}}
						className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition"
					>
						<svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
						</svg>
						Keluar
					</button>
				</div>
			</nav>

			{/* Main Content - Add padding-top on mobile to account for fixed header */}
			<main className="pt-16 sm:pt-0">{children}</main>
		</div>
	);
}
